// BRIEF 107 §2b — ONE DEFINITION OF "THIS ROW HAS SOMETHING".
//
// §1, ruled by Laurent: *"the 'Notes' and 'Documents' icons should not bear a
// number, but become darker (become same color as eg the timer icons) when
// there is something."* And the test, which is the part that matters: *"now one
// has to open all notes systematically to find out whether there is one, same
// would happen to documents when the numbering will be removed."* **A closed row
// must say whether there is anything inside, without being opened.**
//
// §2b: "After §3 there are two icons expressing the same idea on the same row.
//  They must not each carry their own conditional colour — that is the shape
//  this programme has spent the week draining."
//
// So this file owns the CHROME and the TWO STATES, once, and every icon on a
// row that opens a drawer renders through it. The predicate stays with each
// caller — a note has something when its text is non-empty, a documents pane
// when its live list is — and that difference is real. What must not differ is
// how "yes" and "no" LOOK, and that is what lives here.
//
// ── WHY BOTH TOGGLES MOVED HERE RATHER THAN ONE IMPORTING THE OTHER ─────────
//
// 105 put `NotesToggle` in `NotesDrawer.tsx`, beside the drawer it opens, which
// was right when it was the only one. The documents toggle was open-coded three
// times with the same twelve style properties. Making `NotesDrawer` the home of
// a DOCUMENTS toggle would be worse than either. They are one vocabulary — the
// icons that sit at the end of a row's title line — so they live in one file
// named for that, and the drawers keep their own.
//
// ── §4a — AND THE BRIEF'S HOST MAP IS WRONG, WHICH IS WHY THIS IS A COMPONENT ─
//
// §4 says `CaseTaskRow` is mounted on "CV/CCV, the compact column, the
// Dashboard" and `CaseEventRow` on "CV/CCV/column, AgendaView, Dashboard", and
// that "the screens follow from the components". MEASURED — they do not:
//
//   CaseTaskRow    ← CaseTasksSection  (CaseView, CollectiveCaseView)
//                  ← CompactColumn
//   CaseEventRow   ← CaseAgendaSection (CaseView, CollectiveCaseView)
//                  ← CompactColumn
//
// **Neither the Dashboard nor AgendaView mounts either component.** Both write
// their own task rows and event cards, which is exactly why `Dashboard.tsx` has
// its own `<File>` and its own `<NotesToggle>` and why 105 had to reach into it
// separately. So there are FOUR independent row implementations, not two
// components with several hosts — and the screens follow from a SHARED
// CONTROL rather than from a shared row. This file is that control.
//
// ── 118 §9 — HALF OF THE PARAGRAPH ABOVE EXPIRED, AND IS KEPT FOR ITS ARGUMENT ─
//
// BRIEF 115 closed the Dashboard's half: §1 gave its task rows to `CaseTaskRow`
// and §2 its event cards to `CaseEventRow` (Dashboard.tsx:34-35), so that view
// mounts BOTH components and no longer writes its own. AgendaView still writes
// its own event lines, so the count is THREE row implementations, not four. The
// reasoning survives its own numbers: what made this a shared control was never
// how many hosts there were, and 115's merge is the same argument carried one
// level up.
//
// §9 adds the other half of the lesson. "The predicate stays with each caller"
// (above) held for two facts and failed for the third: `hasNotes` was written
// `!!row.notes` at three call sites while the drawer it lights tests
// `initial.trim()`, so a note of blanks lit an icon over a drawer reading "No
// notes." The predicate now lives once, beside the trim rule that defines it —
// `hasNoteText`, NotesDrawer.tsx.
import type { ReactNode } from "react";
import { NotebookPen, File } from "lucide-react";
import { useTranslation } from "../i18n";
import { RAD, FS } from "../lib/tokens";
import { hasNoteText } from "./NotesDrawer"; // 118 §9

/**
 * ── §2a — THE COLOUR, READ FROM THE TIMER ICONS RATHER THAN CHOSEN ──────────
 *
 * "take the colour from the timer icons, by reading them … They take their
 *  colour from a class rather than an inline value. Resolve what they actually
 *  render and use that, whether it turns out to be `--ac` or something else."
 *
 * MEASURED, and the premise in the middle of that sentence is false in a way
 * that matters: `.btn` (App.css:390) declares **no `color` at all**, and both
 * timer controls set one INLINE. They are also not one colour:
 *
 *   the Play triangle, at rest   `var(--tp)`   CaseTaskRow's `tdTimer`
 *   the Clock (log time)         `var(--ts)`   the button immediately beside it
 *
 * So "the timer icons" spans two values and the brief's instruction does not
 * pick between them. `themeDerivation.ts` does. **`--tp` is "Primary text" and
 * is EDITABLE; `--ts` and `--tm` are DERIVED FROM IT** by mixing toward the
 * surface `--bs` — `--ts` at t=0.30 and `--tm` at t=0.55 on a dark surface,
 * 0.20 / 0.35 on a light one (`themeDerivation.ts:196-197`). One ramp, three
 * stops, and only the first is a colour anybody chose.
 *
 * That settles it twice over. The "nothing" state has always been `--tm`, which
 * IS `--tp` faded 55% into the background — so the state's own opposite is the
 * same colour at full strength, not a new one. And full strength is exactly
 * what the resting timer triangle renders, which is what Laurent named.
 *
 * It also makes "become DARKER" literally true rather than approximately.
 * `--tp` is derived nowhere and set per theme, so on a LIGHT surface it is
 * near-black while `--tm` is a mid grey pulled toward white; on the dark theme
 * the same rule reads as fuller contrast. One value, both themes, and the word
 * he used is correct on the one he was looking at.
 *
 * WHAT IT REPLACES IS `--ac`, which 105 chose without this argument. That is
 * the accent, and 99 §2a has already ruled what the accent says on these very
 * surfaces: *"an accent border reads as SELECTION, not as hover … the accent is
 * the one the app uses for 'this is chosen'."* An icon lit in the selection
 * colour says the row is picked, not that it holds something — two vocabularies
 * for two different facts, which is the collision that ruling exists to stop.
 *
 * §6's first stop does not fire. This is the app's primary text colour on the
 * app's row background: the pairing every task title and every event title on
 * every one of these rows already renders. The icon sits on the ROW, not on the
 * drawer's tinted wash, so the drawer's background is not in the question.
 */
export const DRAWER_ICON_HAS = "var(--tp)";
export const DRAWER_ICON_EMPTY = "var(--tm)";

/**
 * The chrome, once. Twelve style properties that were written four times.
 *
 * `has` is the whole of §1: it decides the colour and nothing else does.
 * `open` keeps the `--as` wash 105 §2b left as the only open/closed signal
 * after the arrow went.
 */
export default function RowDrawerToggle({
  icon,
  has,
  open,
  onToggle,
  title,
}: {
  icon: ReactNode;
  has: boolean;
  open: boolean;
  /** absent → INERT (195 §A, the Archive's cards): the icon still says the
   *  row has a note or documents, and nothing opens */
  onToggle?: () => void;
  title?: string;
}) {
  return (
    <button
      type="button"
      className="btn"
      style={{
        padding: "0 3px",
        background: open ? "var(--as)" : "none",
        border: "none",
        borderRadius: RAD.r4,
        color: has ? DRAWER_ICON_HAS : DRAWER_ICON_EMPTY,
        cursor: onToggle ? "pointer" : "default",
        display: "inline-flex",
        alignItems: "center",
        gap: 2,
        fontSize: FS.s9,
        fontFamily: "'JetBrains Mono',monospace",
        flexShrink: 0,
        lineHeight: 1.4,
      }}
      title={title}
      aria-disabled={onToggle ? undefined : true}
      tabIndex={onToggle ? undefined : -1}
      onClick={(e) => {
        e.stopPropagation();
        if (onToggle) onToggle();
      }}
    >
      {icon}
    </button>
  );
}

/**
 * ── §3b-iii (105), MOVED HERE WITH THE TOGGLE IT GOVERNS ────────────────────
 *
 * "An icon that opens an empty drawer, or no icon at all. Either is defensible;
 *  an icon that does nothing is not."
 *
 * The icon is shown when there is something to READ or something to WRITE:
 *
 *   something there, whoever you are  → icon, lit; the drawer shows it
 *   nothing, and you may write        → icon, muted; the drawer opens EMPTY and
 *                                       editable
 *   nothing, and you may not          → NO ICON. Nothing to show, nothing to do.
 *
 * BRIEF 107 §2b — and the DOCUMENTS rule beside it is the same rule, which is
 * the point: it was already `liveDocs.length > 0 || docsCanTouch`, written out
 * at three call sites. Same shape, same order, now the same sentence.
 */
/* 118 §9 — the trim was ALREADY right here, and that is the sharp end of the
   finding: this file got "is there a note" correct for whether the icon EXISTS
   and the three call sites got it wrong for what COLOUR it is. One spelling
   now; the rule lives in `NotesDrawer` with the value it is about.
   NOTE: this function has had no call sites since 109 §1d made the notes icon
   unconditional. Left standing rather than deleted — that is a cleanup and not
   §9's sentence — but it may not carry a second spelling of the rule while it
   stands. */
export function notesToggleVisible(notes: unknown, canWrite: boolean) {
  return hasNoteText(notes) || !!canWrite;
}

export function docsToggleVisible(liveDocs: unknown[] | null | undefined, canTouch: boolean) {
  return (Array.isArray(liveDocs) ? liveDocs.length > 0 : false) || !!canTouch;
}

/** The notes icon. §1's `has` is "there is a non-empty note". */
export function NotesToggle({
  open,
  hasNotes,
  onToggle,
}: {
  open: boolean;
  hasNotes: boolean;
  onToggle?: () => void; // 195 §A — absent → inert (RowDrawerToggle)
}) {
  const { t } = useTranslation();
  return (
    <RowDrawerToggle
      icon={<NotebookPen size={11} />}
      has={hasNotes}
      open={open}
      onToggle={onToggle}
      title={t("notes.toggle_tip", { fallback: "Show notes" })}
    />
  );
}

/**
 * The documents icon. §1's `has` is "there is at least one live document" —
 * which is what the DIGIT used to say, and §3a is why it no longer has to.
 */
export function DocsToggle({
  open,
  hasDocs,
  onToggle,
}: {
  open: boolean;
  hasDocs: boolean;
  onToggle?: () => void; // 195 §A — absent → inert (RowDrawerToggle)
}) {
  const { t } = useTranslation();
  return (
    <RowDrawerToggle
      icon={<File size={11} />}
      has={hasDocs}
      open={open}
      onToggle={onToggle}
      title={t("task.docs.toggle_tip", { fallback: "Show documents" })}
    />
  );
}
